import { useCallback, useEffect, useState } from "react";
import {
  generateNotifications,
  getNotifications,
  markRead as markReadById,
  markAllRead,
  clearAll,
} from "@/lib/notifications-service";
import { supabase } from "@/lib/supabase";
import { db } from "@/lib/db";

const SCHEDULE_INTERVAL_MS = 5 * 60 * 1000; // re-check every 5 min

export function useNotifications(districtId) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!districtId) return;
    const items = await getNotifications(districtId);
    setNotifications(items);
    setLoading(false);
  }, [districtId]);

  const schedule = useCallback(async () => {
    if (!districtId) return;
    await generateNotifications(districtId);
    await load();
  }, [districtId, load]);

  // Immediate load from Dexie on mount — shows persisted notifications before
  // generateNotifications() finishes rebuilding from visits/prayers/people data.
  // schedule() runs in background and calls load() again when done.
  useEffect(() => {
    if (!districtId) return;
    load();
    schedule();
  }, [districtId, load, schedule]);

  // Periodic re-schedule
  useEffect(() => {
    if (!districtId) return;
    const t = setInterval(schedule, SCHEDULE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [districtId, schedule]);

  // Clear notifications from Dexie and state on logout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        db.notifications.clear().catch(() => {});
        setNotifications([]);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Mark a single notification as read — called when navigating from the panel.
  const markRead = useCallback(async (id) => {
    await markReadById(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const dismissAll = useCallback(async () => {
    if (!districtId) return;
    await markAllRead(districtId);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [districtId]);

  const clear = useCallback(async () => {
    if (!districtId) return;
    await clearAll(districtId);
    setNotifications([]);
  }, [districtId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return { notifications, loading, unreadCount, markRead, dismissAll, clear, reload: load, refresh: schedule };
}
